import {
  EnvelopeIcon,
  PaperClipIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import {
  MAILING_OPTIONS,
  type MailingOption,
} from '@/components/postgrid/mailing-class/MailingClassStep';
import { useGetBlobsQuery } from '@/services/api/blobService';
import { usePostgridContactsRetrieveQuery } from '@/services/api/postgridService';

import { WizardStep } from './WizardStep';

interface LetterReviewStepProps {
  matterId?: string;
  wizardData?: {
    to_contact_id?: string;
    to_contact_name?: string;
    attachment_blob_ids?: string[];
    mailing_class?: string;
  };
  attachmentCount?: number;
  markStepComplete?: (isValid: boolean) => void;
}

export const LetterReviewStep: React.FC<LetterReviewStepProps> = ({
  matterId,
  wizardData = {},
  attachmentCount = 0,
  markStepComplete,
}) => {
  const { to_contact_id, attachment_blob_ids, mailing_class } = wizardData;

  const { data: contact } = usePostgridContactsRetrieveQuery(
    { id: to_contact_id as string },
    {
      skip: !to_contact_id,
    },
  );

  const { data: blobs } = useGetBlobsQuery(matterId as string, {
    skip: !matterId,
  });

  // Get selected blob objects
  const selectedBlobs = React.useMemo(() => {
    if (!blobs || !attachment_blob_ids) return [];
    return blobs.filter((blob) => attachment_blob_ids.includes(blob.id));
  }, [blobs, attachment_blob_ids]);

  // Get selected mailing option
  const selectedMailingOption = React.useMemo((): MailingOption | null => {
    if (!mailing_class) return null;
    return (
      MAILING_OPTIONS.find(
        (option: MailingOption) => option.value === mailing_class,
      ) || null
    );
  }, [mailing_class]);
  // Validate that we have all required data
  const attachmentsCount = attachment_blob_ids
    ? attachment_blob_ids.length
    : attachmentCount;
  // Require recipient and at least one attachment
  const isValid = Boolean(to_contact_id) && attachmentsCount > 0;

  return (
    <WizardStep
      title="Review Your Letter"
      description="Please review the details below before submitting your letter."
      isValid={isValid}
      markStepComplete={markStepComplete}
    >
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-8">
        <div className="space-y-6">
          {/* Recipient */}
          <div className="bg-white rounded-lg p-4 border border-blue-200">
            <div className="flex items-center mb-3">
              <UserIcon className="h-5 w-5 text-blue-600 mr-2" />
              <h4 className="font-medium text-blue-900">Send To</h4>
            </div>
            {contact ? (
              <div className="text-gray-900">
                <div className="font-medium text-base mb-1">
                  {contact.first_name || contact.company_name || 'Recipient'}
                  {contact.first_name &&
                    contact.last_name &&
                    ` ${contact.last_name}`}
                </div>
                <div className="text-sm text-gray-600 leading-relaxed">
                  {contact.address_line1}
                  <br />
                  {contact.address_line2 && (
                    <>
                      {contact.address_line2}
                      <br />
                    </>
                  )}
                  {contact.city}, {contact.province_or_state}{' '}
                  {contact.postal_or_zip}
                  <br />
                  {contact.country}
                </div>
              </div>
            ) : (
              <div className="text-gray-500">No recipient selected</div>
            )}
          </div>

          {/* Letter Content */}
          <div className="bg-white rounded-lg p-4 border border-blue-200">
            <div className="flex items-center mb-3">
              <PaperClipIcon className="h-5 w-5 text-blue-600 mr-2" />
              <h4 className="font-medium text-blue-900">Letter Content</h4>
            </div>
            {attachmentsCount > 0 ? (
              <div className="space-y-2">
                <div className="text-gray-900 mb-2">
                  {attachmentsCount} document
                  {attachmentsCount !== 1 ? 's' : ''} will be printed and mailed
                  {selectedBlobs.length > 0 ? ':' : ''}
                </div>
                {selectedBlobs.length > 0 ? (
                  <div className="space-y-1">
                    {selectedBlobs.map((blob) => (
                      <div
                        key={blob.id}
                        className="text-sm text-gray-700 flex items-center"
                      >
                        <span className="w-2 h-2 bg-gray-400 rounded-full mr-2 flex-shrink-0" />
                        <span className="truncate">
                          {blob.original_filename || blob.name}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 italic">
                    Document details loading...
                  </div>
                )}
                <div className="text-sm text-gray-600 mt-2">
                  These documents will be the contents of your letter
                </div>
              </div>
            ) : (
              <div className="text-gray-500">
                No documents selected for mailing
              </div>
            )}
          </div>

          {/* Mailing Class */}
          {selectedMailingOption && (
            <div className="bg-white rounded-lg p-4 border border-blue-200">
              <div className="flex items-center mb-3">
                <EnvelopeIcon className="h-5 w-5 text-blue-600 mr-2" />
                <h4 className="font-medium text-blue-900">Mail Service</h4>
              </div>
              <div className="space-y-2">
                <div>
                  <h5 className="font-semibold text-gray-900">
                    {selectedMailingOption.label}
                  </h5>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedMailingOption.description}
                  </p>
                </div>
                <div className="flex items-center text-sm">
                  <span className="font-medium text-gray-700">
                    {selectedMailingOption.deliveryTime}
                  </span>
                </div>
                {selectedMailingOption.features.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {selectedMailingOption.features.map(
                      (feature: string, index: number) => (
                        <span
                          key={index}
                          className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full"
                        >
                          {feature}
                        </span>
                      ),
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="bg-blue-100 rounded-lg p-4 border border-blue-300">
            <div className="text-center">
              <p className="text-blue-800 font-medium">
                ✅ Ready to create and send this letter
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Once submitted, the letter will be processed and mailed to the
                recipient.
              </p>
            </div>
          </div>
        </div>
      </div>
    </WizardStep>
  );
};
